import React, { Fragment, useMemo } from 'react';
import { useIntl } from 'react-intl';
import CollapsibleContainer from '@gin-betting/components/collapsible-container';
import cn from 'classnames';
import { times, upperFirst } from 'lodash';

import messages from 'messages';
import SmartLink from '../SmartLink';
import useLessMoreContainer from './useLessMoreContainer';

import './lessMoreContainer.scss';

type CollapsibleContainerProps = PropsFromReactComponent<
    typeof CollapsibleContainer
>;

interface Props
    extends Omit<CollapsibleContainerProps, 'children' | 'isCollapsed'> {
    initSize: number;
    children: Array<boolean | null | JSX.Element>;
    cellInRow: 1 | 2 | 3 | 4 | 5;
    moreLink?: string;
    columnTitle?: JSX.Element;
}

const LessMoreContainer: React.FC<Props> = ({
    initSize,
    children: items,
    duration,
    transitionTimingFunction,
    cellInRow,
    moreLink,
    columnTitle,
}) => {
    const {
        visibleItems,
        extraItems,
        canCollapse,
        moreCount,
        isCollapsed,
        visibleStubsSize,
        extraStubsSize,
        toggleIsCollapsed,
        validCellInRow,
    } = useLessMoreContainer({
        initSize,
        items,
        cellInRow,
    });
    const { formatMessage } = useIntl();

    const text = isCollapsed
        ? `${formatMessage(messages.over)} +${moreCount}`
        : formatMessage(messages.under);

    const button = useMemo(
        () =>
            moreLink ? (
                <SmartLink styleName="button" to={moreLink}>
                    {upperFirst(text)}
                </SmartLink>
            ) : (
                <div styleName="button" onClick={toggleIsCollapsed}>
                    {upperFirst(text)}
                </div>
            ),
        [text, moreLink, toggleIsCollapsed]
    );

    const visibleStubs = useMemo(
        () =>
            times(visibleStubsSize).map(i => (
                <div
                    key={i}
                    styleName="stub"
                    className="__app-LessMoreContainer-stub"
                />
            )),
        [visibleStubsSize]
    );

    const extraStubs = useMemo(
        () =>
            times(extraStubsSize).map(i => (
                <div
                    key={i}
                    styleName="stub"
                    className="__app-LessMoreContainer-stub"
                >
                    {!isCollapsed && i === 0 && button}
                </div>
            )),
        [extraStubsSize, button, isCollapsed]
    );

    return (
        <div
            styleName={cn('container', {
                'can-collapse': canCollapse,
            })}
        >
            {columnTitle && (
                <div styleName={cn('visible', `cell-in-row-${validCellInRow}`)}>
                    {times(cellInRow).map(idx => (
                        <Fragment key={idx}>{columnTitle}</Fragment>
                    ))}
                </div>
            )}
            <div styleName={cn('visible', `cell-in-row-${validCellInRow}`)}>
                {visibleItems}
                {visibleStubs}
            </div>

            {canCollapse && (
                <Fragment>
                    <CollapsibleContainer
                        isCollapsed={isCollapsed}
                        duration={duration}
                        transitionTimingFunction={transitionTimingFunction}
                    >
                        <div
                            styleName={cn(
                                'extra',
                                `cell-in-row-${validCellInRow}`
                            )}
                        >
                            {extraItems}
                            {extraStubs}
                        </div>
                    </CollapsibleContainer>
                    {(isCollapsed || !extraStubs.length) && button}
                </Fragment>
            )}
        </div>
    );
};

export default React.memo(LessMoreContainer);
